import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { authedAtTimestampAtom } from "../../auth/state";
import {
  clientUserAuthGate,
  telehealthRequiredConsentsWaitingRoomBlockGate,
} from "../../statsig/gates";
import {
  useGetCurrentPatientLazyQuery,
  GetCurrentPatientQueryVariables,
} from "../../generated/graphql";
import { useEnterWaitingRoomCallback as useEnterWaitingRoomCallbackBase } from "../nav/useEnterWaitingRoomCallback";
import { useAppDependenciesContext } from "../../state";
import {
  useLazyClientRequiresAuthedIntroRoom,
  UseLazyClientRequiresAuthedIntroRoomDependencies,
  createClientRequiresAuthedIntroRoomVariables,
} from "../introduction-room/auth/useClientRequiresAuthedIntroRoom";

export const UNVERIFIED = "UNVERIFIED";
type ConsentVerificationStatus = typeof UNVERIFIED | boolean;
export const ADMIT_UNVERIFIED_LOG_MESSAGE =
  "Missing data for authenticated client, admitting unverified client to waiting room";

type Dependencies = {
  useEnterWaitingRoomCallback: typeof useEnterWaitingRoomCallbackBase;
  useLazyClientRequiresAuthedIntroRoomDependencies?: UseLazyClientRequiresAuthedIntroRoomDependencies;
};

/**
 * Returns a callback that gracefully authorizes the current user, checks if the
 * client has all of the required legal agreements signed, and automatically
 * enters the waiting room if successful.
 *
 * @param dependencies - Optional dependency overrides (e.g. for testing)
 */
export function useGracefullyAuthorizeCurrentUserCallback(
  {
    useEnterWaitingRoomCallback,
    useLazyClientRequiresAuthedIntroRoomDependencies,
  }: Dependencies = {
    useEnterWaitingRoomCallback: useEnterWaitingRoomCallbackBase,
  },
) {
  const [getCurrentPatientBase] = useGetCurrentPatientLazyQuery();
  const getClientRequiresAuthedIntroRoom = useLazyClientRequiresAuthedIntroRoom(
    useLazyClientRequiresAuthedIntroRoomDependencies,
  );
  const setAuthedAtTimestamp = useSetAtom(authedAtTimestampAtom);
  const enterWaitingRoom = useEnterWaitingRoomCallback();
  const { logger, Statsig } = useAppDependenciesContext();

  return useCallback(
    async ({
      variables,
      getCurrentPatient = getCurrentPatientBase,
    }: {
      variables: GetCurrentPatientQueryVariables;
      getCurrentPatient?: typeof getCurrentPatientBase;
    }) => {
      const isClientUserAuthEnabled = Statsig.checkGate(clientUserAuthGate);
      if (!isClientUserAuthEnabled) return;

      const { patientShortId } = variables;
      const isRequiredConsentsWaitingRoomBlockEnabled = Statsig.checkGate(
        telehealthRequiredConsentsWaitingRoomBlockGate,
      );

      const handleSuccess = (
        preferredName: string,
        requiresAuthedIntroRoom: ConsentVerificationStatus,
      ) => {
        setAuthedAtTimestamp(Date.now());
        if (requiresAuthedIntroRoom === true) return;
        if (
          requiresAuthedIntroRoom === UNVERIFIED &&
          isRequiredConsentsWaitingRoomBlockEnabled
        ) {
          logger.warn(ADMIT_UNVERIFIED_LOG_MESSAGE, { patientShortId });
        }
        enterWaitingRoom(preferredName);
      };

      let requiresAuthedIntroRoom: ConsentVerificationStatus = UNVERIFIED;
      try {
        const variables =
          createClientRequiresAuthedIntroRoomVariables(patientShortId);
        if (!variables) {
          logger.warn(
            "Missing patientShortId for client requires authed intro room query",
            {
              patientShortId,
            },
          );
        } else {
          const result = await getClientRequiresAuthedIntroRoom({ variables });
          if (result?.data?.clientRequiresAuthedIntroRoom) {
            requiresAuthedIntroRoom =
              result.data.clientRequiresAuthedIntroRoom.requireAuth;
          }
        }
      } catch (err) {
        const error = err as Error;
        // Failure to fetch this data should not block admission to the waiting room
        logger.warn(
          "Failed to fetch client requires authed intro room",
          { patientShortId },
          error,
        );
      }

      try {
        const { data } = await getCurrentPatient({ variables });

        // Not using the `onCompleted` callback because it executes
        // asynchronously which can result in the intro room briefly flashing
        // before the waiting room
        const preferredName = data?.patientInfo?.preferredName;

        if (!preferredName) {
          const errorMsg = "Failed to get patient's preferred name";
          logger.warn(errorMsg, { patientShortId });
          return;
        }
        return handleSuccess(preferredName, requiresAuthedIntroRoom);
      } catch (err) {
        const error = err as Error;
        logger.error("Failed to fetch patient", { patientShortId }, error);
        return;
      }
    },
    [
      Statsig,
      enterWaitingRoom,
      getCurrentPatientBase,
      logger,
      setAuthedAtTimestamp,
      getClientRequiresAuthedIntroRoom,
    ],
  );
}
